import {
  STOCK_ADJUSTMENT_STATUS_LABELS,
  type StockAdjustment,
} from "@artisancode/api-types";
import { Ban } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { formatDateWIB } from "@/lib/date-utils";
import { cn, formatCurrencyRounded } from "@/lib/utils";

import { StockAdjustmentDiffBadge } from "./stock-adjustment-diff-badge";
import {
  getAdjustmentValueImpact,
  getBucketLabel,
} from "./stock-adjustment-utils";

import type { StockAdjustmentRowsProps } from "./stock-adjustment-desktop-table";

export function StockAdjustmentMobileList({
  rows,
  onCancelRequest,
}: StockAdjustmentRowsProps) {
  if (rows.length === 0) {
    return (
      <div className="py-12 text-center text-xs text-muted-foreground">
        Belum ada data penyesuaian stok.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      {rows.map((adj) => (
        <StockAdjustmentCard
          key={adj.id}
          adj={adj}
          onCancelRequest={onCancelRequest}
        />
      ))}
    </div>
  );
}

function StockAdjustmentCard({
  adj,
  onCancelRequest,
}: {
  adj: StockAdjustment;
  onCancelRequest: (adj: StockAdjustment) => void;
}) {
  const statusMeta = STOCK_ADJUSTMENT_STATUS_LABELS[adj.status];
  const valueImpact = getAdjustmentValueImpact(adj);
  const roundedImpact = Math.round(valueImpact);

  return (
    <Card className="gap-2 py-3 shadow-xs">
      <CardContent className="space-y-2.5 px-4 text-xs">
        <div className="flex items-start justify-between gap-2">
          <div>
            <span className="font-mono text-sm font-bold text-foreground">
              {adj.code}
            </span>
            <p className="mt-0.5 text-[11px] text-muted-foreground">
              {formatDateWIB(adj.date)}
              {adj.createdByName && ` • ${adj.createdByName}`}
            </p>
          </div>
          <Badge variant={statusMeta.variant} className="shrink-0 text-[10px]">
            {statusMeta.label}
          </Badge>
        </div>

        <div className="space-y-1 rounded-md bg-muted/40 p-2">
          <div className="flex items-start justify-between gap-2">
            <span className="text-xs font-semibold text-foreground">
              {adj.itemName}
            </span>
            <Badge variant="outline" className="shrink-0 text-[10px]">
              {getBucketLabel(adj.bucket)}
            </Badge>
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            {adj.itemSku && <span className="font-mono">{adj.itemSku}</span>}
            {adj.itemSku && <span>•</span>}
            <span>
              {adj.itemType === "raw_material" ? "Bahan Baku" : "Produk Jadi"}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-1 rounded-md border p-2 text-center text-xs">
          <QtyCell label="Sistem">
            {adj.systemQty.toLocaleString("id-ID")}
          </QtyCell>
          <QtyCell label="Fisik">
            {adj.countedQty.toLocaleString("id-ID")}
          </QtyCell>
          <QtyCell label="Selisih">
            <StockAdjustmentDiffBadge
              difference={adj.difference}
              className="px-1"
            />
          </QtyCell>
        </div>

        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Dampak Nilai:</span>
          <span
            className={cn(
              "font-mono font-semibold",
              roundedImpact < 0 && "text-rose-600 dark:text-rose-400",
              roundedImpact > 0 && "text-emerald-600 dark:text-emerald-400",
              roundedImpact === 0 && "text-muted-foreground",
            )}
          >
            {formatCurrencyRounded(valueImpact)}
          </span>
        </div>

        {(adj.reasonName || adj.note) && (
          <div className="rounded bg-muted/30 p-2 text-[11px] text-muted-foreground">
            {adj.reasonName && (
              <Badge variant="secondary" className="mr-1.5 text-[10px]">
                {adj.reasonName}
              </Badge>
            )}
            <span>{adj.note || "-"}</span>
          </div>
        )}

        {adj.cancelReason && (
          <div className="rounded bg-destructive/10 p-2 text-[11px] text-destructive">
            <span className="font-medium">Alasan Batal: </span>
            <span>{adj.cancelReason}</span>
          </div>
        )}

        {adj.status === "completed" && (
          <div className="flex items-center justify-end border-t border-border/40 pt-2.5">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onCancelRequest(adj)}
              className="min-h-[40px] w-full text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              <Ban className="mr-1 h-3.5 w-3.5" />
              Batalkan Penyesuaian
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function QtyCell({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <span className="block text-[10px] text-muted-foreground">{label}</span>
      <div className="flex justify-center font-mono font-medium">
        {children}
      </div>
    </div>
  );
}
